import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { Button, Chip, DrawerBodyWrapper, DrawerFooterWrapper, SectionHeader, Select, Textarea, TOAST_TYPE, UploadMultipleFileButton } from '@base'
import { strings } from '@constants'
import { ButtonsWrapper, OptionHeading, SectionBreakLine, SubSectionWrapper, VoterHeader, VotersListWrapper } from '../styles'
import { GridListWrapper, Label, TextValue } from '../../styles'
import { getAllEmployees, getVoterByOption } from '@data'
import { showDateFormat } from '@utils'
import { toggleFileViewer } from '@data/state/action/root'
import { addVoterDetails, addVotingDocument } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { User } from '@data/model'
import { useForceUpdate } from '@hooks'

const deriveOptionFormState = () => {
	const state = {
		voter: {
			id: 'voter',
			label: strings('voter'),
			placeholder: strings('select', 'voter'),
			value: '',
			primaryKey: 'user_id',
			displayKey: 'full_name',
			disabled: false,
			required: true,
		},
		voter_remark: {
			id: 'voter-remark',
			label: strings('remark'),
			placeholder: strings('voter', 'remark'),
			value: '',
			disabled: false,
			required: false,
		},
	}

	return state
}

const CreditVote = ({ voteDetails, cranData, chosenOption, update2 }) => {
	const dispatch = useDispatch()
	const { orgId } = useParams()
	const [isAdd, setIsAdd] = useState(false)
	const [forceUpdate, update] = useForceUpdate()
	const [employees, setEmployees] = useState([])
	const [voters, setVoters] = useState([])
	const [formState, setFormState] = useState(deriveOptionFormState())

	const loggedInUser = useSelector((_s) => _s.getIn(['user', 'user']))

	useEffect(() => {
		if (employees?.length === 0) {
			getAllEmployees({})
				.then((response) => {
					let _employeeList = response?.map((item) => new User(item)?.getUserDetails())
					setEmployees(_employeeList)
				})
				.catch((error) => {})
		}
		dispatch(showLoader(true))
		getVoterByOption(orgId, loggedInUser?.user_id, voteDetails?.option_id)
			.then((response) => {
				setVoters(response)
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [cranData, forceUpdate])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const cancelHandler = () => {
		setIsAdd(false)
		setFormState(deriveOptionFormState())
	}

	const addVoter = () => {
		const _obj = {
			voter: formState?.voter?.value,
			voter_remark: formState?.voter_remark?.value,
			voted_by: loggedInUser.getUserDetails(),
			voted_on: new Date().getTime(),
			chosen_option: voteDetails?.option_id,
		}
		dispatch(showLoader(true))
		addVoterDetails(orgId, loggedInUser?.user_id, _obj)
			.then((response) => {
				dispatch(showToast(true, strings('msg_voter_added_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('msg_voter_added_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update2()
				update()
				setIsAdd(false)
				setFormState(deriveOptionFormState())
				dispatch(showLoader(false))
			})
	}

	const addVoteForm = () => {
		if (!isAdd) {
			return null
		}
		return (
			<SubSectionWrapper>
				<Select
					id={formState?.voter?.id}
					label={formState?.voter?.label}
					data={employees}
					placeholder={formState?.voter?.placeholder}
					value={employees?.find((item) => item.user_id === formState?.voter?.value?.user_id) ?? {}}
					displayKey={formState?.voter?.displayKey}
					primaryKey={formState?.voter?.primaryKey}
					disabled={formState?.voter?.disabled}
					required={formState?.voter?.required}
					onChange={updateState('voter')}
				/>
				<Textarea
					id={formState?.voter_remark?.id}
					label={formState?.voter_remark?.label}
					placeholder={formState?.voter_remark?.placeholder}
					value={formState?.voter_remark?.value}
					disabled={formState?.voter_remark?.disabled}
					required={formState?.voter_remark?.required}
					onChange={updateState('voter_remark')}
				/>
				<ButtonsWrapper>
					<Button xs type='secondary' variant='destructive' onClick={cancelHandler}>
						{strings('cancel')}
					</Button>
					<Button xs margin={`0 0 0 auto`} onClick={addVoter}>
						{strings('add')}
					</Button>
				</ButtonsWrapper>
			</SubSectionWrapper>
		)
	}

	const viewVoterDoc = (file) => {
		if (!file) {
			return
		}
		dispatch(toggleFileViewer(true, { files: file }))
	}

	const uploadVoterDoc = (voter) => (files) => {
		if (files.length < 1) {
			return
		}
		const getPath = Array.from(files).map((f) => f)
		dispatch(showLoader(true))
		addVotingDocument(getPath, orgId, loggedInUser?.user_id, voter?.voter?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_document_uploaded'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				update2()
				dispatch(showLoader(false))
			})
	}

	const votersList = () => {
		if (isAdd) {
			return null
		}
		return voters?.map((item) => {
			return (
				<SubSectionWrapper>
					<GridListWrapper itemPerRow={[3, 3, 3]}>
						<div>
							<Label>{strings('voter')}</Label>
							<TextValue>{item?.voter?.full_name}</TextValue>
						</div>
						<div>
							<Label>{strings('voted_on')}</Label>
							<TextValue>{showDateFormat(new Date(item?.voted_on))}</TextValue>
						</div>
						<div>
							<Label>{strings('voted_by')}</Label>
							<TextValue>{item?.voted_by?.full_name}</TextValue>
						</div>
					</GridListWrapper>
					<Label>{strings('remark')}</Label>
					<TextValue>{item?.voter_remark}</TextValue>
					<SectionBreakLine />
					<GridListWrapper>
						<div>{`Documents:`}</div>
						<ButtonsWrapper>
							<Button
								xs
								type='secondary'
								variant='destructive'
								margin={`0 0 0 auto`}
								disabled={!item?.documents}
								onClick={() => viewVoterDoc(item?.documents)}
							>
								{strings('view')}
							</Button>
							<UploadMultipleFileButton
								label={strings('upload')}
								xs
								type='primary'
								acceptFile={'image/*'}
								onChange={(files) => uploadVoterDoc(item)(files)}
							/>
						</ButtonsWrapper>
					</GridListWrapper>
				</SubSectionWrapper>
			)
		})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<SectionHeader>
					<OptionHeading>
						{`Option : `}

						<Chip label={voteDetails?.option} status={'paid'} />
					</OptionHeading>
				</SectionHeader>
				<VoterHeader>
					{isAdd ? 'Add Vote' : 'Votes:'}
					{!isAdd && (
						<Button xs margin={`0 0 0 auto`} onClick={() => setIsAdd(true)}>
							{strings(['add', 'new', 'vote'])}
						</Button>
					)}
				</VoterHeader>
				{addVoteForm()}
				<VotersListWrapper>{votersList()}</VotersListWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default CreditVote
